import React, {
  CSSProperties,
  FC,
  ReactElement,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
  memo,
} from "react";
import { createPortal } from "react-dom";
import classes from "./style.module.scss";

type PopoverProps = {
  element?: ReactElement;
  children: ReactNode;
  className?: string;
  maxHeight?: number;
  style?: CSSProperties;
  open: boolean;
  onClose?: () => void;
  displayPosition?: "bottom" | "right" | "left";
  strictPosition?: boolean;
  fullWidth?: boolean;
};

type Position = {
  animation: string;
  top: number;
  left: number;
  width?: number;
  maxHeight: number;
};

const VIEWPORT_PADDING = 8;

const Popover: FC<PopoverProps> = ({
  element,
  children,
  className = "",
  maxHeight,
  style = {},
  open = false,
  onClose,
  displayPosition = "bottom",
  strictPosition = false,
  fullWidth = false,
}) => {
  const containerRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [position, setPosition] = useState<Position>({
    animation: "sliderDown",
    top: 0,
    left: 0,
    maxHeight: 0,
  });

  const [isPositioned, setIsPositioned] = useState(false);

  useLayoutEffect(() => {
    if (!open) {
      setIsPositioned(false);
      return;
    }

    const updatePosition = () => {
      const popup = popoverRef.current;
      const anchor = containerRef.current;

      if (!popup || !anchor) return;

      const anchorRect = anchor.getBoundingClientRect();
      const popupRect = popup.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const availableWidth = Math.max(0, viewportWidth - VIEWPORT_PADDING * 2);

      const availableHeight = Math.max(
        0,
        viewportHeight - VIEWPORT_PADDING * 2,
      );

      const popupWidth = Math.min(popupRect.width, availableWidth);

      const popupHeight = Math.min(
        popup.scrollHeight,
        maxHeight ?? availableHeight,
        availableHeight,
      );

      let left = anchorRect.left;
      let top = anchorRect.bottom;
      let animation = "sliderDown";

      if (displayPosition === "bottom") {
        left = fullWidth
          ? anchorRect.left
          : anchorRect.left + anchorRect.width / 2 - popupWidth / 2;

        const spaceBelow =
          viewportHeight - anchorRect.bottom - VIEWPORT_PADDING;
        const spaceAbove = anchorRect.top - VIEWPORT_PADDING;

        if (
          !strictPosition &&
          popupHeight > spaceBelow &&
          spaceAbove > spaceBelow
        ) {
          top = anchorRect.top - popupHeight;
          animation = "sliderUp";
        } else {
          top = anchorRect.bottom;
          animation = "sliderDown";
        }
      } else if (displayPosition === "right") {
        left = anchorRect.right;
        top = anchorRect.top - popupHeight / 2;
        animation = "sliderleftToRight";

        if (left + popupWidth > viewportWidth - VIEWPORT_PADDING) {
          left = anchorRect.left - popupWidth;
          animation = "sliderRightToLeft";
        }
      } else if (displayPosition === "left") {
        left = anchorRect.left - popupWidth;
        top = anchorRect.top - popupHeight / 2;
        animation = "sliderRightToLeft";

        if (left < VIEWPORT_PADDING) {
          left = anchorRect.right;
          animation = "sliderleftToRight";
        }
      }

      // Keep the popup within the horizontal viewport.
      left = Math.max(
        VIEWPORT_PADDING,
        Math.min(left, viewportWidth - popupWidth - VIEWPORT_PADDING),
      );

      // Keep the popup within the vertical viewport.
      top = Math.max(
        VIEWPORT_PADDING,
        Math.min(top, viewportHeight - popupHeight - VIEWPORT_PADDING),
      );

      setPosition({
        animation,
        top,
        left,
        width: fullWidth ? anchorRect.width : undefined,
        maxHeight: popupHeight,
      });

      setIsPositioned(true);
    };

    const frame = requestAnimationFrame(updatePosition);

    const popup = popoverRef.current;

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updatePosition)
        : null;

    if (popup) {
      resizeObserver?.observe(popup);
    }

    if (containerRef.current) {
      resizeObserver?.observe(containerRef.current);
    }

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, displayPosition, strictPosition, fullWidth, maxHeight]);

  return (
    <>
      {element ? (
        React.cloneElement(element, {
          ref: containerRef,
        } as React.RefAttributes<HTMLElement>)
      ) : (
        <span ref={containerRef as React.RefObject<HTMLSpanElement>} />
      )}

      {open &&
        createPortal(
          <>
            {/* Outside click closes the popover */}
            <div
              className={classes.popoverOverlay}
              data-open="true"
              onClick={() => onClose?.()}
            />

            <div
              ref={popoverRef}
              className={`fixed shadow-md ${classes.popover} ${className}`}
              data-open="true"
              data-animation={position.animation}
              style={{
                ...style,
                top: position.top,
                left: position.left,
                width: position.width ?? style.width,
                maxHeight: position.maxHeight,
                maxWidth: `calc(100vw - ${VIEWPORT_PADDING * 2}px)`,
                overflow: "hidden",
                boxSizing: "border-box",
                visibility: isPositioned ? "visible" : "hidden",
              }}
            >
              {children}
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default memo(Popover);
